import { dayOfWeek, parseISO, planItems, planStrip, type ScheduleResult } from "@clarent/scheduling";
import { slotStyle } from "../lib/plan";
import { PROCESS_META, processKind } from "../lib/process";

const weekday = new Intl.DateTimeFormat("en-GB", { weekday: "short", timeZone: "UTC" });

/**
 * Schedule strip from design system v2.1 ("Backwards · fits"): one column per calendar day
 * from the latest start to the due date, bars sized by the days they take.
 */
export function PlanStrip({ result, workingDays }: { result: ScheduleResult; workingDays: readonly number[] }) {
  const items = planItems(result, workingDays);
  const strip = planStrip(result, workingDays);
  const n = strip.days.length;
  // Labels get cramped past two weeks: drop the weekday, then keep only the ends.
  const showWeekday = n <= 14;
  const showDay = (i: number) => n <= 28 || i === 0 || i === n - 1;

  // Header and bars share one grid, so a bar spanning days lines up with their columns.
  const grid = { display: "grid", gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, columnGap: 4 };

  const kinds = new Set(strip.runs.map((r) => r.slot.kind));
  const firstProcess = items.find((i) => i.kind === "process");
  const processDot = firstProcess ? PROCESS_META[processKind(firstProcess.name ?? "")].solid : "bg-heat";

  return (
    <div className="flex flex-col gap-2.5 rounded-tile border border-line bg-bg p-4">
      <div style={grid}>
        {strip.days.map((d, i) => {
          const weekend = !workingDays.includes(dayOfWeek(d));
          const tone = i === 0 ? "text-brand-dark" : i === n - 1 ? "text-risk-text" : weekend ? "text-subtle" : "text-ink";
          return (
            <div key={d} className={`flex min-w-0 flex-col items-center gap-0.5 ${tone}`}>
              {showWeekday && <span className="text-[10px] leading-3 font-bold uppercase">{weekday.format(new Date(parseISO(d)))}</span>}
              <span className="font-mono text-[13px] leading-4 font-bold">{showDay(i) ? Number(d.slice(8)) : ""}</span>
            </div>
          );
        })}
      </div>
      <div style={grid} className="h-[34px]">
        {strip.runs.map((run) => {
          const label =
            run.slot.kind === "machining"
              ? run.length > 1
                ? "Machining"
                : "Mach."
              : run.slot.kind === "process" && run.length > 1
                ? items[run.slot.item]?.name
                : undefined;
          return (
            <div
              key={`${run.slot.kind}-${run.offset}`}
              style={{ gridColumn: `${run.offset + 1} / span ${run.length}`, gridRow: 1 }}
              className={`flex min-w-0 items-center justify-center overflow-hidden rounded-sm px-1 text-[11px] leading-[14px] font-bold whitespace-nowrap ${slotStyle(run.slot, items).bar}`}
            >
              {label}
            </div>
          );
        })}
      </div>
      <ul className="flex flex-wrap gap-x-3.5 gap-y-1 pt-0.5 text-[11px] leading-[14px] text-muted">
        {kinds.has("machining") && <Key className="bg-ink">Working days</Key>}
        {(kinds.has("courier_out") || kinds.has("courier_back")) && <Key className="courier-box">Courier</Key>}
        {kinds.has("process") && <Key className={processDot}>Calendar days</Key>}
        {kinds.has("due") && <Key className="bg-risk">Due</Key>}
      </ul>
    </div>
  );
}

function Key({ className, children }: { className: string; children: string }) {
  return (
    <li className="flex items-center gap-1.5">
      <span className={`size-2.5 shrink-0 rounded-[3px] ${className}`} />
      {children}
    </li>
  );
}
